import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';

import { gengql } from '@/__generated__';
import { GroupRole } from '@/__generated__/graphql';
import { useAppSelector } from '@/store';

// The viewer's OWN role is derived by scoping the member fetch to the viewer's
// user id rather than paging the full roster. On teams larger than the old
// `limit: 1000` page, an Owner/Admin outside the first 1000 returned rows would
// resolve to a null role and get the Account scope silently hidden (ENG-5256).
//
// This used to scope by `name`, which the server resolves through a MySQL
// FULLTEXT match. InnoDB never indexes tokens shorter than
// innodb_ft_min_token_size (3), so a viewer whose first AND last name are both
// short ("Li Wu") had no indexed tokens and matched nothing — reintroducing the
// exact null-role bug for those users (ENG-5285). `user_id` is an exact match on
// the (team_id, user_id) unique key: one row, no fuzziness, any name.
const VIEWER_GROUP_ROLE = gengql(/* GraphQL */ `
  query viewerGroupRole($groupId: String!, $userId: String!) {
    groupMembers(group_id: $groupId, user_id: $userId, limit: 1, states: [active]) {
      edges {
        node {
          id
          role
          user {
            id
          }
        }
      }
    }
  }
`);

export interface ViewerGroupRoleResult {
  role: GroupRole | null;
  isMember: boolean;
  loading: boolean;
}

export function useViewerGroupRole(groupId: string | undefined): ViewerGroupRoleResult {
  const viewerId = useAppSelector((state) => state.viewer.id);

  const { data, loading, error } = useQuery(VIEWER_GROUP_ROLE, {
    variables: { groupId: groupId!, userId: viewerId! },
    skip: !groupId || !viewerId,
    fetchPolicy: 'cache-and-network',
  });

  // Failing closed is correct, but it makes a broken lookup indistinguishable from a
  // legitimate "not a member" — and it hides the Account scope from every Owner/Admin
  // on the team. A webapp deploy landing ahead of the API (or an API rollback) rejects
  // this query with `Unknown argument "user_id"`, so the degradation has to be
  // alertable rather than silent. Warning, not error: the fail-closed path is intended.
  useEffect(() => {
    if (!error) return;
    Sentry.captureException(error, {
      level: 'warning',
      tags: { viewer_group_role_lookup: 'failed' },
      extra: { groupId },
    });
  }, [error, groupId]);

  return useMemo(() => {
    const edges = data?.groupMembers?.edges ?? [];
    // Redundant now that the server filters by user id, and deliberately kept:
    // it is what guarantees a server-side filter regression can only ever HIDE
    // a role, never grant one. Don't remove it as a cleanup.
    const me = edges.find((e) => e?.node?.user?.id === viewerId)?.node;
    const role = me?.role ?? null;
    return {
      role,
      isMember: role === GroupRole.Member,
      loading,
    };
  }, [data, loading, viewerId]);
}
